import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { Box, Button } from '@mui/material';

import { ExpressModal } from '@features/FindSpecialist/monitoring/components/modals/ExpressModal';
import { MonitoringModal } from '@features/FindSpecialist/monitoring/components/modals/MonitoringModal';
import { useTruncate } from '@hooks/useTruncate';
import { mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import express from 'assets/express.png';
import findSpecialist from 'assets/find-specialist.png';
import monitoring from 'assets/monitoring.png';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonitoringStore } from 'services/monitoring';
import { FindSpecialistModal } from './components/FindSpecialistModal';

const styles = {
	container: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		boxShadow: 3,
		bgcolor: 'white',
		borderRadius: 3,
	},
};

export const FindSpecialist = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const size = useWindowSize();
	const { truncate } = useTruncate();

	const { clearStore } = useMonitoringStore();

	const [openMonitoringModal, setOpenMonitoringModal] = useState(false);
	const [openExpressModal, setOpenExpressModal] = useState(false);
	const [openFindSpecialistModal, setOpenFindSpecialistModal] = useState(false);

	const closeMonitoringModal = () => setOpenMonitoringModal(false);
	const closeExpressModal = () => setOpenExpressModal(false);
	const closeFindSpecialistModal = () => setOpenFindSpecialistModal(false);

	usePageTitle(t('services'));
	useAppbarTitle('services');

	const handleExpressRequest = () => {
		localStorage.removeItem('requestId');

		return navigateWithLanguage('/farmer/services/express/search');
	};

	const handleMonitoringRequest = () => {
		localStorage.removeItem('initialStepDataId');
		clearStore();

		return navigateWithLanguage('/farmer/services/monitoring/packages');
	};

	return (
		<Box
			sx={{
				...styles.container,
				minHeight: size.width > 768 ? '100%' : 'auto',
			}}
		>
			<div className="max-w-6xl mx-auto p-[20px] sm:p-[65px]">
				<h2 className="text-2xl font-bold text-center mb-6">{t('servicesLookingFor')}</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div className="rounded-[12px] overflow-hidden shadow-lg bg-gray-100 md:col-span-1">
						<div
							className="w-full h-full bg-cover bg-center"
							style={{ backgroundImage: `url(${monitoring})` }}
						>
							<div className="h-full bg-black bg-opacity-5 p-[37px] flex flex-col justify-center gap-[20px]">
								<h3 className="text-body-text font-roboto text-2xl font-bold leading-normal uppercase">
									{t('servicesCards.monitoring.title')}
								</h3>
								<p className="text-body-text font-roboto text-base font-normal leading-normal">
									{truncate(t('servicesCards.monitoring.description'), 70)}{' '}
									<span
										onClick={() => {
											setOpenMonitoringModal(true);
										}}
										className="font-roboto text-dark text-base font-bold underline decoration-solid decoration-dark decoration-1 underline-offset-auto cursor-pointer"
									>
										{t('learnMore')}
									</span>
								</p>
								<Button
									variant="contained"
									onClick={handleMonitoringRequest}
									className="text-white px-4 py-2 rounded-lg hover:bg-green-700 w-fit"
									endIcon={<Icon path={mdiChevronRight} size={1} />}
								>
									{t('requestNow')}
								</Button>
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-6 md:col-span-1">
						<div className="flex-1 rounded-[12px] overflow-hidden shadow-lg bg-gray-100">
							<div
								className="w-full h-full bg-cover bg-center"
								style={{ backgroundImage: `url(${express})` }}
							>
								<div className="h-full p-6 flex flex-col justify-center md:gap-[10px] sm:gap-[10px] gap-[10px]">
									<h3 className="text-body-text text-white font-roboto text-2xl font-bold leading-normal uppercase">
										{t('servicesCards.express.title')}
									</h3>
									<p className="text-body-text text-white font-roboto text-base font-normal leading-normal">
										{truncate(t('servicesCards.express.description'), 70)}{' '}
										<span
											onClick={() => {
												setOpenExpressModal(true);
											}}
											className="font-roboto text-white text-base font-bold underline decoration-solid decoration-white decoration-1 underline-offset-auto cursor-pointer"
										>
											{t('learnMore')}
										</span>
									</p>
									<Button
										variant="contained"
										className="text-white px-4 py-2 rounded-lg hover:bg-green-700 w-fit"
										onClick={handleExpressRequest}
										endIcon={<Icon path={mdiChevronRight} size={1} />}
									>
										{t('requestNow')}
									</Button>
								</div>
							</div>
						</div>
						<div className="flex-1 rounded-[12px] overflow-hidden shadow-lg bg-gray-100">
							<div
								className="w-full h-full bg-cover bg-center"
								style={{ backgroundImage: `url(${findSpecialist})` }}
							>
								<div className="h-full p-6 flex flex-col justify-center md:gap-[10px] sm:gap-[10px] gap-[10px]">
									<h3 className="text-body-text text-white font-roboto text-2xl font-bold leading-normal uppercase">
										{t('servicesCards.oneTime.title')}
									</h3>
									<p className="text-body-text text-white font-roboto text-base font-normal leading-normal">
										{truncate(t('servicesCards.oneTime.description'), 70)}{' '}
										<span
											onClick={() => {
												setOpenFindSpecialistModal(true);
											}}
											className="font-roboto text-white text-base font-bold underline decoration-solid decoration-white decoration-1 underline-offset-auto cursor-pointer"
										>
											{t('learnMore')}
										</span>
									</p>
									<Button
										variant="contained"
										className="text-white px-4 py-2 rounded-lg hover:bg-green-700 w-fit"
										endIcon={<Icon path={mdiChevronRight} size={1} />}
										onClick={() =>
											navigateWithLanguage('/farmer/services/specialist-request/search')
										}
									>
										{t('requestNow')}
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<MonitoringModal
				open={openMonitoringModal}
				onClose={closeMonitoringModal}
				description={t('servicesCards.monitoring.description')}
			/>
			<ExpressModal
				open={openExpressModal}
				onClose={closeExpressModal}
				description={t('servicesCards.express.description')}
			/>
			<FindSpecialistModal
				open={openFindSpecialistModal}
				onClose={closeFindSpecialistModal}
				description={t('servicesCards.oneTime.description')}
			/>
		</Box>
	);
};
